import { IdentificationRequirement } from "./models";

/**
 * Canonical identification type names as returned by the API.
 * These names are "more stable" than IDs which vary between environments.
 */
export const IdentificationTypeName = {
  SouthAfricanId: "South African ID",
  Passport: "Passport",
  SATemporaryResidentPermit: "SA Temporary Residence Permit",
  AsylumSeekerPermit: "Asylum Seeker Permit",
  RefugeePermit: "Refugee Permit",
} as const;

export type IdentificationTypeName =
  (typeof IdentificationTypeName)[keyof typeof IdentificationTypeName];

export interface IdentificationTypeConfig {
  name: IdentificationTypeName;
  expiryDate: IdentificationRequirement;
  issueDate: IdentificationRequirement;
  issuingCountry: IdentificationRequirement;
  tempResidenceVisa: IdentificationRequirement;
}

/**
 * Shoprite identification types for the foreign complete-registration flow.
 * South African customers enter a national ID during initial registration
 * and never see this list. Defines which ID types are supported and their
 * field requirements.
 */
export const SR_COMPLETE_REGISTRATION_FOREIGN_ID_TYPES: IdentificationTypeConfig[] =
  [
    {
      name: IdentificationTypeName.Passport,
      expiryDate: "required",
      issueDate: "required",
      issuingCountry: "required",
      tempResidenceVisa: "not-required",
    },
    {
      name: IdentificationTypeName.SATemporaryResidentPermit,
      expiryDate: "required",
      issueDate: "not-required",
      issuingCountry: "required",
      tempResidenceVisa: "not-required",
    },
  ];

/**
 * Tourvest identification types for the foreign complete-registration flow.
 * South African customers enter a national ID during initial registration
 * and never see this list. SA Temporary Residence Permit is Shoprite-only
 * — not valid for Tourvest.
 */
export const TV_COMPLETE_REGISTRATION_FOREIGN_ID_TYPES: IdentificationTypeConfig[] =
  [
    {
      name: IdentificationTypeName.Passport,
      expiryDate: "required",
      issueDate: "required",
      issuingCountry: "required",
      tempResidenceVisa: "required",
    },
    {
      name: IdentificationTypeName.AsylumSeekerPermit,
      expiryDate: "required",
      issueDate: "optional",
      issuingCountry: "required",
      tempResidenceVisa: "not-required",
    },
    {
      name: IdentificationTypeName.RefugeePermit,
      expiryDate: "required",
      issueDate: "optional",
      issuingCountry: "required",
      tempResidenceVisa: "not-required",
    },
  ];
